import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
    Paper, Chip, Divider, Button, IconButton, Tooltip, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Collapse, Dialog, DialogContent, DialogActions, CircularProgress,
    InputAdornment, Switch, FormControlLabel, MenuItem,
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import LinearProgress from '@material-ui/core/LinearProgress'
import Skeleton from '@material-ui/lab/Skeleton'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import RefreshIcon from '@material-ui/icons/Refresh'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import ScheduleIcon from '@material-ui/icons/Schedule'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import TimerIcon from '@material-ui/icons/Timer'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import TimelineIcon from '@material-ui/icons/Timeline'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import MemoryIcon from '@material-ui/icons/Memory'
import WarningIcon from '@material-ui/icons/Warning'
import CloudOffIcon from '@material-ui/icons/CloudOff'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import moment from 'moment'
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement,
    LineElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js'
import { Bar, Line } from 'react-chartjs-2'
import HttpService from '../../services/httpService'
import { setSnackInfo } from '../../redux/actions/appAction'

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend)

// ---------------------------------------------------------------------------
// Tokens
//
// Status colours are reserved for status and never reused as "series 3". Every
// place one appears it carries an icon and a word too, so the state is never
// communicated by colour alone.
// ---------------------------------------------------------------------------
const ORANGE = '#F47E44'
const ORANGE_DARK = '#c1531f'
const ORANGE_LIGHT = '#fff1ea'
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`
const INK = '#2f2a27'
const INK_SOFT = '#6b6460'
const INK_MUTED = '#9a938e'
const SURFACE = '#ffffff'
const RULE = '#ece8e4'

const GREEN = '#17803d'
const GREEN_SOFT = '#e8f5ec'
const RED = '#c62828'
const RED_SOFT = '#fdeaea'
const AMBER = '#b26a00'
const AMBER_SOFT = '#fff4e2'
const SLATE = '#8a8a8a'
const SLATE_SOFT = '#f1f1f1'

const STATUS = {
    success: { label: 'Success', color: GREEN, soft: GREEN_SOFT, Icon: CheckCircleIcon },
    failed: { label: 'Failed', color: RED, soft: RED_SOFT, Icon: ErrorIcon },
    skipped: { label: 'Skipped', color: AMBER, soft: AMBER_SOFT, Icon: RemoveCircleIcon },
    unknown: { label: 'No runs', color: SLATE, soft: SLATE_SOFT, Icon: HelpOutlineIcon },
}
const statusOf = (s) => STATUS[s] || STATUS.unknown

const CHARTS_PREF_KEY = 'cronMonitor.showCharts'

const WINDOWS = [
    { hours: 6, label: 'Last 6 hours' },
    { hours: 24, label: 'Last 24 hours' },
    { hours: 48, label: 'Last 48 hours' },
    { hours: 24 * 7, label: 'Last 7 days' },
    { hours: 24 * 30, label: 'Last 30 days' },
]

const AUTO_REFRESH_MS = 60 * 1000

// ---------------------------------------------------------------------------
// Cron helpers
//
// The schedule string is stored with every run, so the page can say when a job
// was meant to fire and how many runs the window should have held. Anything it
// can't parse falls back to showing the raw expression - a wrong guess here
// would be worse than no guess.
// ---------------------------------------------------------------------------
const expandField = (field, min, max) => {
    if (field === '*') {
        const all = []
        for (let i = min; i <= max; i += 1) all.push(i)
        return all
    }
    const out = []
    field.split(',').forEach((part) => {
        const step = part.includes('/') ? Number(part.split('/')[1]) : 1
        const range = part.split('/')[0]
        let from = min
        let to = max
        if (range !== '*') {
            if (range.includes('-')) {
                const [a, b] = range.split('-')
                from = Number(a); to = Number(b)
            } else {
                from = Number(range); to = Number(range)
            }
        }
        if (Number.isNaN(from) || Number.isNaN(to) || Number.isNaN(step) || step < 1) return
        for (let i = from; i <= to; i += step) out.push(i)
    })
    return out.length ? [...new Set(out)].sort((a, b) => a - b) : null
}

const parseCron = (cron) => {
    if (!cron || typeof cron !== 'string') return null
    const parts = cron.trim().split(/\s+/)
    if (parts.length < 5) return null
    const [m, h, dom, mon, dow] = parts
    const minutes = expandField(m, 0, 59)
    const hoursList = expandField(h, 0, 23)
    if (!minutes || !hoursList) return null
    // Only the everyday case is modelled; a day-of-month or month restriction
    // makes "expected runs" unreliable, so we say so instead of guessing.
    const everyDay = (dom === '*' && mon === '*')
    const dowList = dow === '*' ? null : expandField(dow, 0, 6)
    const daysPerWeek = dowList ? dowList.length : 7
    return {
        minutes,
        hours: hoursList,
        everyDay,
        runsPerDay: everyDay ? (minutes.length * hoursList.length * daysPerWeek) / 7 : null,
    }
}

const hhmm = (h, m) => moment({ hour: h, minute: m }).format('h:mm A')

const describeSchedule = (cron) => {
    const parsed = parseCron(cron)
    if (!parsed || !parsed.everyDay) return cron || '-'
    const { minutes, hours } = parsed
    if (minutes.length > 4 || hours.length > 6) return cron
    if (hours.length === 24 && minutes.length === 1) return `Every hour at :${String(minutes[0]).padStart(2, '0')}`
    const times = []
    hours.forEach((h) => minutes.forEach((m) => times.push(hhmm(h, m))))
    if (!times.length) return cron
    if (times.length === 1) return `Daily at ${times[0]}`
    return `Daily at ${times.join(', ')}`
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
const fmtDuration = (ms) => {
    if (ms === null || ms === undefined) return '-'
    if (ms < 1000) return `${ms} ms`
    if (ms < 60 * 1000) return `${(ms / 1000).toFixed(1)} s`
    const mins = Math.floor(ms / 60000)
    const secs = Math.round((ms % 60000) / 1000)
    return `${mins}m ${secs}s`
}

const fmtWhen = (d) => (d ? moment(d).format('DD MMM, h:mm A') : '-')
const fmtAgo = (d) => (d ? moment(d).fromNow() : 'never')

const BrandProgress = withStyles(() => ({
    root: { height: 3, borderRadius: 999, backgroundColor: 'rgba(193,83,31,0.10)' },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress)

const useStyles = makeStyles(() => ({
    // Sized to the viewport so the page never scrolls - the table body does.
    // 144px is the app chrome: antd Header 64 + Content margin 16*2 + padding
    // 24*2 (components/sidebar.js).
    root: {
        height: 'calc(100vh - 144px)',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '4px 4px 6px',
        color: INK,
        '@media (max-width: 960px)': {
            height: 'auto', minHeight: '100vh', overflow: 'visible', padding: '4px 4px 32px',
        },
    },

    banner: {
        flexShrink: 0,
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16,
        background: HEADER_GRADIENT, borderRadius: 16, padding: '18px 26px',
        marginBottom: 16, color: '#fff',
        boxShadow: '0 10px 24px -14px rgba(193,83,31,0.75)',
    },
    bannerIcon: {
        width: 46, height: 46, borderRadius: 14, display: 'grid', placeItems: 'center',
        background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.28)',
    },
    bannerTitle: { fontWeight: 700, fontSize: 20, lineHeight: 1.2, letterSpacing: 0.2 },
    bannerSub: { fontSize: 12.5, opacity: 0.92, marginTop: 3 },
    bannerSpacer: { flex: 1 },
    bannerControl: {
        background: 'rgba(255,255,255,0.16)', borderRadius: 10, padding: '2px 10px',
        border: '1px solid rgba(255,255,255,0.26)',
        '& .MuiInputBase-input': { color: '#fff', fontSize: 13, fontWeight: 600, paddingTop: 7, paddingBottom: 7 },
        '& .MuiSelect-icon': { color: '#fff' },
        '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' },
    },
    // Same white-on-gradient treatment as MarketingJunk, redirectEC and the
    // execution dashboard. The outlined version read as disabled against the
    // orange banner.
    bannerBtn: {
        background: '#fff',
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 13,
        borderRadius: 10,
        textTransform: 'none',
        padding: '6px 16px',
        boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
        '&:hover': { background: ORANGE_LIGHT },
        '&.Mui-disabled': { background: 'rgba(255,255,255,0.8)', color: ORANGE_DARK, boxShadow: 'none' },
    },
    // Sits immediately right of Refresh, so the timestamp and the control that
    // changes it read as one unit instead of the time hiding in the subtitle.
    bannerUpdated: {
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.88)',
        whiteSpace: 'nowrap', marginLeft: 2,
    },
    liveDot: {
        width: 7, height: 7, borderRadius: '50%', background: '#8ff0a4',
        boxShadow: '0 0 0 3px rgba(143,240,164,0.25)', flexShrink: 0,
    },
    autoLabel: { color: '#fff', fontSize: 12.5, marginRight: 0, marginLeft: 0 },

    tiles: { flexShrink: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 10 },
    tile: {
        background: SURFACE, border: `1px solid ${RULE}`, borderRadius: 12, padding: '9px 12px',
        position: 'relative', overflow: 'hidden',
    },
    tileAccent: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 4 },
    tileLabel: { fontSize: 11.5, letterSpacing: 0.5, textTransform: 'uppercase', color: INK_MUTED, fontWeight: 700 },
    tileValue: { fontSize: 22, fontWeight: 700, lineHeight: 1.1, marginTop: 2 },
    tileHint: { fontSize: 10.5, color: INK_SOFT, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },

    panels: { flexShrink: 0, display: 'grid', gridTemplateColumns: '1.45fr 1fr', gap: 10, marginBottom: 10, '@media (max-width: 1100px)': { gridTemplateColumns: '1fr' } },
    panel: { background: SURFACE, border: `1px solid ${RULE}`, borderRadius: 12, padding: '10px 14px 8px' },
    panelTitle: { fontSize: 13.5, fontWeight: 700, color: INK, display: 'flex', alignItems: 'center', gap: 7 },
    panelSub: { fontSize: 11.5, color: INK_MUTED, marginTop: 2, marginBottom: 10 },
    chartBox: { height: '16vh', minHeight: 110, maxHeight: 190 },
    chartEmpty: {
        height: '16vh', minHeight: 110, maxHeight: 190, display: 'grid', placeItems: 'center', textAlign: 'center',
        color: INK_MUTED, fontSize: 13, gap: 6,
    },

    toolbar: {
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: SURFACE, border: `1px solid ${RULE}`, borderRadius: 14,
        flexShrink: 0, padding: '8px 12px', marginBottom: 10,
    },
    search: { minWidth: 240, flex: '0 1 320px' },
    filterChip: { fontWeight: 600, fontSize: 12.5, borderRadius: 9, height: 30 },
    toolbarSpacer: { flex: 1 },
    // Filled, not outlined. As an outline it read as a third disabled-looking
    // control in a row of inputs and nobody would find it.
    chartsToggle: {
        textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: 999,
        color: '#fff', background: ORANGE_DARK, padding: '4px 14px',
        whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(193,83,31,0.28)',
        '&:hover': { background: '#a8441a' },
    },
    // One line under the charts saying the control exists, shown only while
    // they are visible - once hidden, the button itself says "Show charts".
    //
    // Given its own tinted callout: as plain muted text it sat between two
    // cards and read as a caption belonging to the chart above it.
    chartsHint: {
        flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 12, color: '#7a4b00',
        background: AMBER_SOFT, border: `1px solid ${AMBER}44`,
        borderRadius: 10, padding: '7px 12px', margin: '0 0 10px',
    },
    progressSlot: { flexShrink: 0, height: 3, marginBottom: 8 },
    loadingNote: {
        flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 9,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
        borderRadius: 10, padding: '7px 12px', marginBottom: 10,
        fontSize: 12, color: '#7a6a60',
        '& b': { color: ORANGE_DARK },
    },

    tableWrap: {
        background: SURFACE, border: `1px solid ${RULE}`, borderRadius: 14, overflow: 'hidden',
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        '@media (max-width: 960px)': { flex: 'none', display: 'block' },
    },
    // Brand orange, flat - the same header treatment as MarketingJunk, teams,
    // vendors and the execution dashboard, so the monitor reads as part of the
    // same product rather than a grey utility screen.
    th: {
        fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
        color: '#fff', backgroundColor: ORANGE_DARK, borderBottom: 'none',
        padding: '11px 14px', whiteSpace: 'nowrap',
        // stickyHeader paints its own background on the head cells, so this has
        // to sit above the scrolling rows or they show through at the seam.
        zIndex: 3,
    },
    // The run-history table lives INSIDE an expanded row of the table above it.
    // Repeating the solid orange there would make the child look like a second
    // page-level table, so it gets the tinted version of the same colour.
    thSub: {
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
        color: ORANGE_DARK, backgroundColor: ORANGE_LIGHT,
        borderBottom: `1px solid ${ORANGE}33`,
        padding: '9px 14px', whiteSpace: 'nowrap',
        zIndex: 2,
    },
    td: { fontSize: 13, color: INK, borderBottom: `1px solid ${RULE}`, padding: '11px 14px', verticalAlign: 'middle' },
    tdTight: { padding: 0, borderBottom: 'none' },
    row: { cursor: 'pointer', transition: 'background 120ms', '&:hover': { background: '#fdfbfa' } },
    rowOpen: { background: '#fdf6f2' },

    jobName: { fontWeight: 650, fontSize: 13.5, lineHeight: 1.25 },
    jobKey: { fontSize: 11, color: INK_MUTED, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', marginTop: 2 },

    statusChip: { height: 26, fontWeight: 700, fontSize: 12, borderRadius: 8, paddingLeft: 2 },

    strip: { display: 'flex', gap: 3, alignItems: 'center' },
    stripCell: { width: 9, height: 20, borderRadius: 3, display: 'inline-block' },
    stripEmpty: { fontSize: 11.5, color: INK_MUTED },
    rerunBtn: {
        textTransform: 'none', fontSize: 12.5, fontWeight: 700, borderRadius: 9,
        background: ORANGE_DARK, color: '#fff', padding: '3px 12px', whiteSpace: 'nowrap',
        '&:hover': { background: '#a8441a' },
        '&.Mui-disabled': { background: '#e6e2df', color: '#9a938e' },
    },
    confirmBox: {
        display: 'flex', gap: 12, alignItems: 'flex-start',
        background: AMBER_SOFT, border: `1px solid ${AMBER}44`, borderRadius: 12,
        padding: '12px 14px', marginTop: 14,
    },
    confirmNote: { fontSize: 12.5, color: '#7a4b00', lineHeight: 1.5 },

    rateWrap: { minWidth: 92 },
    rateText: { fontSize: 12.5, fontWeight: 700, marginBottom: 4 },
    rateBar: { height: 6, borderRadius: 999, background: '#eeeae7', overflow: 'hidden' },
    rateFill: { height: '100%', borderRadius: 999 },

    detail: { background: '#fdfbfa', borderBottom: `1px solid ${RULE}`, padding: '16px 18px' },
    detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14, marginBottom: 12 },
    detailLabel: { fontSize: 11, letterSpacing: 0.4, textTransform: 'uppercase', color: INK_MUTED, fontWeight: 700, marginBottom: 3 },
    detailValue: { fontSize: 13, color: INK, wordBreak: 'break-word' },
    mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 },
    errorBox: {
        background: RED_SOFT, border: `1px solid ${RED}33`, borderRadius: 10,
        padding: '10px 12px', color: '#8e1f1f', fontSize: 12.5, whiteSpace: 'pre-wrap',
        wordBreak: 'break-word', marginBottom: 12,
    },
    responseBox: {
        background: '#f6f5f4', border: `1px solid ${RULE}`, borderRadius: 10,
        padding: '10px 12px', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: 180, overflow: 'auto', marginBottom: 12,
    },

    emptyState: { textAlign: 'center', padding: '54px 24px', color: INK_SOFT },
    emptyTitle: { fontWeight: 700, fontSize: 16, color: INK, marginTop: 10 },
    emptyBody: { fontSize: 13.5, marginTop: 8, lineHeight: 1.6, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' },
    code: {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5,
        background: '#f4f2f0', border: `1px solid ${RULE}`, borderRadius: 8, padding: '2px 7px',
    },

    // display:flex so the X can sit at the end of the bar. The title block gets
    // minWidth:0 or a long endpoint pushes the close button off the edge.
    dialogHead: {
        background: HEADER_GRADIENT, color: '#fff', padding: '16px 22px',
        display: 'flex', alignItems: 'flex-start', gap: 12,
    },
    dialogHeadText: { minWidth: 0, flex: 1 },
    dialogTitle: { fontWeight: 700, fontSize: 17 },
    dialogSub: { fontSize: 12.5, opacity: 0.92, marginTop: 3, wordBreak: 'break-all' },
    dialogClose: {
        color: '#fff', flexShrink: 0, marginTop: -2,
        transition: 'background-color 200ms ease, transform 200ms ease',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)', transform: 'rotate(90deg)' },
    },
}))

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
const StatusChip = ({ status, classes, small }) => {
    const s = statusOf(status)
    const { Icon } = s
    return (
        <Chip
            size={small ? 'small' : 'medium'}
            className={classes.statusChip}
            style={{ background: s.soft, color: s.color, border: `1px solid ${s.color}33` }}
            icon={<Icon style={{ color: s.color, fontSize: 16 }} />}
            label={s.label}
        />
    )
}

const StatTile = ({ classes, label, value, hint, color }) => (
    <div className={classes.tile}>
        <div className={classes.tileAccent} style={{ background: color }} />
        <div className={classes.tileLabel}>{label}</div>
        <div className={classes.tileValue} style={{ color }}>{value}</div>
        {hint ? <div className={classes.tileHint}>{hint}</div> : null}
    </div>
)

// Last 20 runs, oldest on the left. Each cell carries a tooltip with the
// status in words, so the colour is a shortcut and not the only carrier.
const RunStrip = ({ runs, classes }) => {
    if (!runs || !runs.length) return <span className={classes.stripEmpty}>no runs</span>
    const ordered = [...runs].reverse()
    return (
        <div className={classes.strip}>
            {ordered.map((r, i) => {
                const s = statusOf(r.status)
                return (
                    <Tooltip
                        key={`${r.startedAt}-${i}`}
                        title={`${s.label} · ${fmtWhen(r.startedAt)} · ${fmtDuration(r.durationMs)}`}
                        arrow
                    >
                        <span className={classes.stripCell} style={{ background: s.color, opacity: 0.9 }} />
                    </Tooltip>
                )
            })}
        </div>
    )
}

const SuccessRate = ({ job, classes }) => {
    if (job.successRate === null || job.successRate === undefined) return <span className={classes.stripEmpty}>-</span>
    const color = job.successRate === 100 ? GREEN : job.successRate >= 80 ? AMBER : RED
    return (
        <div className={classes.rateWrap}>
            <div className={classes.rateText} style={{ color }}>{job.successRate}%</div>
            <div className={classes.rateBar}>
                <div className={classes.rateFill} style={{ width: `${job.successRate}%`, background: color }} />
            </div>
            <div className={classes.tileHint}>{job.runs} run{job.runs === 1 ? '' : 's'}</div>
        </div>
    )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const CronJobMonitor = (props) => {
    const classes = useStyles()
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [data, setData] = useState(null)
    const [hours, setHours] = useState(24)
    const [triggering, setTriggering] = useState(null)   // jobName currently running
    const [confirmJob, setConfirmJob] = useState(null)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [sortBy, setSortBy] = useState('attention')
    // Lazy initialiser so the stored value is read once, not on every render.
    // Wrapped because localStorage throws in private mode and this is a
    // preference, not something worth taking the page down for.
    const [showCharts, setShowCharts] = useState(() => {
        try { return localStorage.getItem(CHARTS_PREF_KEY) !== '0' } catch (e) { return true }
    })
    const [expanded, setExpanded] = useState(null)
    const [autoRefresh, setAutoRefresh] = useState(true)
    const [fetchedAt, setFetchedAt] = useState(null)

    const [historyJob, setHistoryJob] = useState(null)
    const [history, setHistory] = useState([])
    const [historyLoading, setHistoryLoading] = useState(false)

    const timer = useRef(null)

    const load = useCallback(async (silent) => {
        if (silent) setRefreshing(true); else setLoading(true)
        try {
            const res = await HttpService.getCronRunDashboard(hours)
            setData(res.data)
            setFetchedAt(new Date())
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not load the cron dashboard', 'error')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [hours]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(false) }, [load])

    useEffect(() => {
        if (timer.current) clearInterval(timer.current)
        if (autoRefresh) timer.current = setInterval(() => load(true), AUTO_REFRESH_MS)
        return () => { if (timer.current) clearInterval(timer.current) }
    }, [autoRefresh, load])

    const jobs = useMemo(() => (data && data.jobs) || [], [data])

    // Expected-vs-actual run count, from the cron expression stored with the
    // runs. A job that silently stopped firing has no failed row to show - the
    // rows just stop - so this is the only place that gap becomes visible.
    const enriched = useMemo(() => jobs.map((job) => {
        const parsed = parseCron(job.schedule)
        const expected = parsed && parsed.runsPerDay
            ? Math.max(1, Math.round((parsed.runsPerDay * (data?.windowHours || hours)) / 24))
            : null
        // One short is ordinary - the window rarely lines up with the schedule.
        const missed = expected !== null && job.runs < expected - 1 ? expected - job.runs : 0
        return { ...job, expected, missed, scheduleText: describeSchedule(job.schedule) }
    }), [jobs, data, hours])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        let out = enriched.filter((job) => {
            if (statusFilter === 'failed' && job.lastStatus !== 'failed') return false
            if (statusFilter === 'success' && job.lastStatus !== 'success') return false
            if (statusFilter === 'skipped' && job.lastStatus !== 'skipped') return false
            if (statusFilter === 'missed' && !job.missed) return false
            if (!q) return true
            return `${job.label} ${job.jobName} ${job.endpoint || ''}`.toLowerCase().includes(q)
        })
        const rank = (j) => (j.lastStatus === 'failed' ? 0 : j.missed ? 1 : j.lastStatus === 'skipped' ? 2 : 3)
        out = [...out].sort((a, b) => {
            if (sortBy === 'attention') {
                if (rank(a) !== rank(b)) return rank(a) - rank(b)
                return new Date(b.lastRunAt) - new Date(a.lastRunAt)
            }
            if (sortBy === 'name') return String(a.label).localeCompare(String(b.label))
            if (sortBy === 'failures') return b.failures - a.failures
            if (sortBy === 'slowest') return (b.avgDurationMs || 0) - (a.avgDurationMs || 0)
            return new Date(b.lastRunAt) - new Date(a.lastRunAt)
        })
        return out
    }, [enriched, search, statusFilter, sortBy])

    const totals = (data && data.totals) || { jobs: 0, failingNow: 0, runs: 0, failures: 0 }
    const healthy = enriched.filter((j) => j.lastStatus === 'success' && !j.missed).length
    const missedJobs = enriched.filter((j) => j.missed).length
    const failureRate = totals.runs ? Math.round((totals.failures / totals.runs) * 100) : 0

    // --- charts -------------------------------------------------------------
    const timeline = (data && data.timeline) || []
    const timelineLabels = timeline.map((t) => (
        data?.bucket === 'hour'
            ? moment(t.bucket, 'YYYY-MM-DD HH:mm').format('DD MMM, h A')
            : moment(t.bucket, 'YYYY-MM-DD').format('DD MMM')
    ))
    const hasSkipped = timeline.some((t) => t.skipped > 0)
    const timelineData = {
        labels: timelineLabels,
        datasets: [
            { label: 'Success', data: timeline.map((t) => t.success), backgroundColor: GREEN, borderRadius: 3, maxBarThickness: 16 },
            { label: 'Failed', data: timeline.map((t) => t.failed), backgroundColor: RED, borderRadius: 3, maxBarThickness: 16 },
            ...(hasSkipped ? [{ label: 'Skipped', data: timeline.map((t) => t.skipped), backgroundColor: AMBER, borderRadius: 3, maxBarThickness: 16 }] : []),
        ],
    }
    const timelineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        // Spacers between stacked segments, so adjacent fills never touch.
        datasets: { bar: { borderWidth: 2, borderColor: SURFACE } },
        scales: {
            x: { stacked: true, grid: { display: false }, ticks: { color: INK_MUTED, font: { size: 10 }, maxRotation: 0, autoSkipPadding: 14 } },
            y: { stacked: true, beginAtZero: true, grid: { color: '#f0eeec' }, border: { display: false }, ticks: { color: INK_MUTED, font: { size: 10 }, precision: 0 } },
        },
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: 'circle', color: INK_SOFT, font: { size: 11.5 } } },
            tooltip: { backgroundColor: '#2f2a27', padding: 10, cornerRadius: 8, titleFont: { size: 12 }, bodyFont: { size: 12 } },
        },
    }

    const failing = [...enriched].filter((j) => j.failures > 0).sort((a, b) => b.failures - a.failures).slice(0, 8)
    const failuresData = {
        labels: failing.map((j) => j.label),
        datasets: [{ label: 'Failed runs', data: failing.map((j) => j.failures), backgroundColor: RED, borderRadius: 3, maxBarThickness: 14 }],
    }
    const failuresOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { beginAtZero: true, grid: { color: '#f0eeec' }, border: { display: false }, ticks: { color: INK_MUTED, font: { size: 10 }, precision: 0 } },
            y: { grid: { display: false }, ticks: { color: INK_SOFT, font: { size: 11 } } },
        },
        plugins: {
            legend: { display: false },
            tooltip: { backgroundColor: '#2f2a27', padding: 10, cornerRadius: 8 },
        },
    }

    // --- drill-down ---------------------------------------------------------
    const openHistory = async (job) => {
        setHistoryJob(job)
        setHistoryLoading(true)
        setHistory([])
        try {
            const res = await HttpService.getCronRunHistory({ jobName: job.jobName, limit: 100 })
            setHistory(res.data.runs || [])
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not load the run history', 'error')
        } finally {
            setHistoryLoading(false)
        }
    }

    // Deep link from the failure alert mail: /cron-job-monitor?job=autoOpenPO
    // filters to that job and opens its history, so the mail lands the reader
    // on the thing it was about rather than on the full list.
    const deepLinkHandled = useRef(false)
    useEffect(() => {
        if (deepLinkHandled.current || !enriched.length) return
        const wanted = new URLSearchParams(window.location.search).get('job')
        if (!wanted) return
        deepLinkHandled.current = true
        setSearch(wanted)
        const match = enriched.find((j) => j.jobName === wanted)
        if (match) {
            setExpanded(match.jobName)
            openHistory(match)
        }
        // openHistory is stable for this purpose and intentionally left out of
        // the deps - including it would re-run this on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enriched])

    // Only a run that did not complete can be re-run. A job whose last run
    // succeeded has nothing to repeat, and several of these jobs send real
    // messages - firing one twice is worse than waiting for the schedule.
    const canRerun = (job) => job.lastStatus === 'failed' || job.lastStatus === 'skipped'

    const runJobNow = async (job) => {
        setConfirmJob(null)
        setTriggering(job.jobName)
        try {
            const res = await HttpService.triggerCronJob(job.jobName)
            const d = res.data || {}
            props.setSnackInfo(d.message || 'Job triggered', d.status === 'success' ? 'success' : 'error')
            // Reload silently so the new row shows up in the strip and the
            // counts without the whole page flashing back to skeletons.
            load(true)
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not trigger the job', 'error')
        } finally {
            setTriggering(null)
        }
    }

    const durationSeries = useMemo(() => {
        const ordered = [...history].reverse().filter((r) => r.durationMs !== undefined && r.durationMs !== null)
        return {
            labels: ordered.map((r) => moment(r.startedAt).format('DD MMM, h:mm A')),
            datasets: [{
                label: 'Duration',
                data: ordered.map((r) => Math.round(r.durationMs / 1000)),
                borderColor: ORANGE_DARK,
                backgroundColor: ORANGE_DARK,
                borderWidth: 2,
                pointRadius: 3,
                pointHoverRadius: 5,
                // Status of each point still reads from the table below; the
                // line carries timing only.
                tension: 0.25,
            }],
        }
    }, [history])

    const durationOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { grid: { display: false }, ticks: { color: INK_MUTED, font: { size: 10 }, maxRotation: 0, autoSkipPadding: 20 } },
            y: { beginAtZero: true, grid: { color: '#f0eeec' }, border: { display: false }, ticks: { color: INK_MUTED, font: { size: 10 }, callback: (v) => `${v}s` } },
        },
        plugins: {
            legend: { display: false },
            tooltip: { backgroundColor: '#2f2a27', padding: 10, cornerRadius: 8, callbacks: { label: (c) => `${c.parsed.y}s` } },
        },
    }

    const copy = (text) => {
        if (!text) return
        navigator.clipboard?.writeText(text)
            .then(() => props.setSnackInfo('Copied', 'success'))
            .catch(() => props.setSnackInfo('Could not copy', 'error'))
    }

    // --- render -------------------------------------------------------------
    const filterChips = [
        { key: 'all', label: `All (${enriched.length})`, color: INK_SOFT },
        { key: 'failed', label: `Failing (${totals.failingNow})`, color: RED },
        { key: 'missed', label: `Missed runs (${missedJobs})`, color: AMBER },
        { key: 'success', label: `Healthy (${healthy})`, color: GREEN },
    ]

    return (
        <div className={classes.root}>
            <div className={classes.banner}>
                <div className={classes.bannerIcon}><MemoryIcon style={{ fontSize: 26 }} /></div>
                <div>
                    <div className={classes.bannerTitle}>Cron Job Monitor</div>
                    <div className={classes.bannerSub}>
                        Scheduled API calls from the cron server — what ran, what failed, and why
                    </div>
                </div>
                <div className={classes.bannerSpacer} />
                <TextField
                    select
                    value={hours}
                    onChange={(e) => setHours(Number(e.target.value))}
                    className={classes.bannerControl}
                >
                    {WINDOWS.map((w) => <MenuItem key={w.hours} value={w.hours}>{w.label}</MenuItem>)}
                </TextField>
                <FormControlLabel
                    className={classes.autoLabel}
                    control={
                        <Switch
                            size="small"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            style={{ color: '#fff' }}
                        />
                    }
                    label="Auto"
                />
                <Button
                    size="small"
                    className={classes.bannerBtn}
                    // Spinner in the button's own colour now that the fill is white.
                    startIcon={refreshing
                        ? <CircularProgress size={14} thickness={5} style={{ color: ORANGE_DARK }} />
                        : <RefreshIcon />}
                    onClick={() => load(true)}
                    disabled={refreshing}
                >
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                </Button>
                {fetchedAt && (
                    <div className={classes.bannerUpdated}>
                        <span className={classes.liveDot} />
                        {/* Keeps showing the last successful fetch while a new
                            one runs - the button already says "Refreshing…",
                            and repeating it here would say nothing extra. */}
                        Updated {moment(fetchedAt).format('h:mm:ss A')}
                    </div>
                )}
            </div>

            {/* Reserved slot rather than a conditional element, so an auto
                refresh does not jog the tiles up and down every minute. */}
            <div className={classes.progressSlot}>
                {(loading || refreshing) ? <BrandProgress /> : null}
            </div>

            {/* The bar alone says "something is happening"; this says what,
                and names the window so a slow 30-day pull is explained. */}
            {(loading || refreshing) && (
                <div className={classes.loadingNote}>
                    <CircularProgress size={15} thickness={5} style={{ color: ORANGE_DARK }} />
                    <span>
                        <b>{refreshing ? 'Refreshing cron runs…' : 'Fetching cron runs…'}</b>
                        {' '}Reading every recorded run from the {WINDOWS.find((w) => w.hours === hours)?.label.toLowerCase() || 'selected window'}.
                    </span>
                </div>
            )}

            {loading ? (
                <>
                    <div className={classes.tiles}>
                        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rect" height={96} style={{ borderRadius: 14 }} />)}
                    </div>
                    <Skeleton variant="rect" height={260} style={{ borderRadius: 14, marginBottom: 16 }} />
                    <Skeleton variant="rect" height={320} style={{ borderRadius: 14 }} />
                </>
            ) : !enriched.length ? (
                <Paper elevation={0} className={classes.tableWrap}>
                    <div className={classes.emptyState}>
                        <CloudOffIcon style={{ fontSize: 46, color: INK_MUTED }} />
                        <div className={classes.emptyTitle}>No runs recorded yet</div>
                        <div className={classes.emptyBody}>
                            Nothing has been written to <span className={classes.code}>/api/cronRunLog</span> in this window.
                            That is expected until the cron server starts reporting: add <span className={classes.code}>cronLogger.js</span> there,
                            wrap one job with <span className={classes.code}>callAndLog(...)</span>, and the first row appears the next time it fires.
                            <br /><br />
                            If jobs were reporting before, check that the cron server is running and that its
                            <span className={classes.code}> CRON_LOG_API_KEY</span> still matches the ERP's.
                        </div>
                    </div>
                </Paper>
            ) : (
                <>
                    <div className={classes.tiles}>
                        <StatTile classes={classes} label="Jobs tracked" value={totals.jobs} hint={`in the ${WINDOWS.find((w) => w.hours === hours)?.label.toLowerCase() || 'window'}`} color={INK} />
                        <StatTile classes={classes} label="Failing now" value={totals.failingNow} hint={totals.failingNow ? 'last run did not succeed' : 'last run succeeded everywhere'} color={totals.failingNow ? RED : GREEN} />
                        <StatTile classes={classes} label="Missed runs" value={missedJobs} hint={missedJobs ? 'fired fewer times than scheduled' : 'all jobs fired on schedule'} color={missedJobs ? AMBER : GREEN} />
                        <StatTile classes={classes} label="Total runs" value={totals.runs} hint={`${totals.failures} failed`} color={INK} />
                        <StatTile classes={classes} label="Failure rate" value={`${failureRate}%`} hint={failureRate === 0 ? 'clean window' : 'of all runs in window'} color={failureRate === 0 ? GREEN : failureRate < 10 ? AMBER : RED} />
                    </div>

                    {showCharts && <div className={classes.panels}>
                        <Paper elevation={0} className={classes.panel}>
                            <div className={classes.panelTitle}><TimelineIcon style={{ fontSize: 18, color: ORANGE_DARK }} /> Runs over time</div>
                            <div className={classes.panelSub}>
                                Every recorded run, bucketed by {data?.bucket === 'hour' ? 'hour' : 'day'} (IST)
                            </div>
                            <div className={timeline.length ? classes.chartBox : classes.chartEmpty}>
                                {timeline.length
                                    ? <Bar data={timelineData} options={timelineOptions} />
                                    : <><HelpOutlineIcon style={{ fontSize: 30, color: INK_MUTED }} /><div>No runs in this window</div></>}
                            </div>
                        </Paper>

                        <Paper elevation={0} className={classes.panel}>
                            <div className={classes.panelTitle}><WarningIcon style={{ fontSize: 18, color: RED }} /> Failures by job</div>
                            <div className={classes.panelSub}>Jobs with at least one failed run, worst first</div>
                            <div className={failing.length ? classes.chartBox : classes.chartEmpty}>
                                {failing.length
                                    ? <Bar data={failuresData} options={failuresOptions} />
                                    : <><CheckCircleIcon style={{ fontSize: 30, color: GREEN }} /><div>No failures in this window</div></>}
                            </div>
                        </Paper>
                    </div>}

                    {showCharts && (
                        <div className={classes.chartsHint}>
                            <InfoOutlinedIcon style={{ fontSize: 14 }} />
                            Short on space? <b>Hide charts</b> below roughly doubles the rows in the table.
                        </div>
                    )}

                    <div className={classes.toolbar}>
                        <TextField
                            className={classes.search}
                            variant="outlined"
                            size="small"
                            placeholder="Search job or endpoint"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: INK_MUTED }} /></InputAdornment>,
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearch('')}><ClearIcon style={{ fontSize: 16 }} /></IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        {filterChips.map((c) => (
                            <Chip
                                key={c.key}
                                className={classes.filterChip}
                                label={c.label}
                                clickable
                                variant={statusFilter === c.key ? 'default' : 'outlined'}
                                onClick={() => setStatusFilter(c.key)}
                                style={statusFilter === c.key
                                    ? { background: c.color, color: '#fff', borderColor: c.color }
                                    : { color: c.color, borderColor: `${c.color}55` }}
                            />
                        ))}
                        <div className={classes.toolbarSpacer} />
                        {/* Charts are context; the table is what gets acted on.
                            Hiding them hands their height to the rows, and the
                            choice sticks per browser. */}
                        <Tooltip arrow title={showCharts ? 'Hide the charts and give the table about twice as many rows' : 'Bring the timeline and failure charts back'}>
                        <Button
                            size="small"
                            className={classes.chartsToggle}
                            startIcon={showCharts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => {
                                const next = !showCharts
                                setShowCharts(next)
                                try { localStorage.setItem(CHARTS_PREF_KEY, next ? '1' : '0') } catch (e) { /* private mode */ }
                            }}
                        >
                            {showCharts ? 'Hide charts' : 'Show charts'}
                        </Button>
                        </Tooltip>
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            label="Sort"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            style={{ minWidth: 170 }}
                        >
                            <MenuItem value="attention">Needs attention</MenuItem>
                            <MenuItem value="lastRunAt">Most recent run</MenuItem>
                            <MenuItem value="failures">Most failures</MenuItem>
                            <MenuItem value="slowest">Slowest</MenuItem>
                            <MenuItem value="name">Name</MenuItem>
                        </TextField>
                    </div>

                    <Paper elevation={0} className={classes.tableWrap}>
                        {/* flex:1 + minHeight:0 so the body scrolls inside the
                            card, and stickyHeader so the column titles stay put
                            while it does. */}
                        <TableContainer style={{ flex: 1, minHeight: 0 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.th} style={{ width: 42 }} />
                                        <TableCell className={classes.th} style={{ width: 54 }}>Sl No</TableCell>
                                        <TableCell className={classes.th}>Job</TableCell>
                                        <TableCell className={classes.th}>Last run</TableCell>
                                        <TableCell className={classes.th}>Schedule</TableCell>
                                        <TableCell className={classes.th}>Last 20 runs</TableCell>
                                        <TableCell className={classes.th}>Success rate</TableCell>
                                        <TableCell className={classes.th}>Duration</TableCell>
                                        <TableCell className={classes.th} align="right">History</TableCell>
                                        <TableCell className={classes.th} align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!filtered.length && (
                                        <TableRow>
                                            <TableCell className={classes.td} colSpan={10} align="center" style={{ padding: 34, color: INK_MUTED }}>
                                                No job matches this filter.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filtered.map((job, index) => {
                                        const open = expanded === job.jobName
                                        return (
                                            <React.Fragment key={job.jobName}>
                                                <TableRow
                                                    className={`${classes.row} ${open ? classes.rowOpen : ''}`}
                                                    onClick={() => setExpanded(open ? null : job.jobName)}
                                                >
                                                    <TableCell className={classes.td}>
                                                        <IconButton size="small">
                                                            {open ? <KeyboardArrowUpIcon style={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon style={{ fontSize: 18 }} />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell className={classes.td} style={{ color: INK_MUTED, whiteSpace: 'nowrap' }}>
                                                        {index + 1}
                                                    </TableCell>
                                                    <TableCell className={classes.td}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <StatusChip status={job.lastStatus} classes={classes} small />
                                                            <div>
                                                                <div className={classes.jobName}>{job.label}</div>
                                                                <div className={classes.jobKey}>{job.jobName}</div>
                                                            </div>
                                                            {job.missed ? (
                                                                <Tooltip arrow title={`Expected about ${job.expected} runs in this window, recorded ${job.runs}. The job may not be firing.`}>
                                                                    <Chip
                                                                        size="small"
                                                                        className={classes.statusChip}
                                                                        icon={<WarningIcon style={{ color: AMBER, fontSize: 15 }} />}
                                                                        label={`${job.missed} missed`}
                                                                        style={{ background: AMBER_SOFT, color: AMBER, border: `1px solid ${AMBER}33` }}
                                                                    />
                                                                </Tooltip>
                                                            ) : null}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className={classes.td}>
                                                        <div style={{ fontWeight: 600 }}>{fmtAgo(job.lastRunAt)}</div>
                                                        <div className={classes.tileHint}>{fmtWhen(job.lastRunAt)}</div>
                                                    </TableCell>
                                                    <TableCell className={classes.td}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <ScheduleIcon style={{ fontSize: 15, color: INK_MUTED }} />
                                                            <span>{job.scheduleText}</span>
                                                        </div>
                                                        <div className={`${classes.tileHint} ${classes.mono}`}>{job.schedule || '-'}</div>
                                                    </TableCell>
                                                    <TableCell className={classes.td}><RunStrip runs={job.recent} classes={classes} /></TableCell>
                                                    <TableCell className={classes.td}><SuccessRate job={job} classes={classes} /></TableCell>
                                                    <TableCell className={classes.td}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <TimerIcon style={{ fontSize: 15, color: INK_MUTED }} />
                                                            <span style={{ fontWeight: 600 }}>{fmtDuration(job.lastDurationMs)}</span>
                                                        </div>
                                                        <div className={classes.tileHint}>avg {fmtDuration(job.avgDurationMs)}</div>
                                                    </TableCell>
                                                    <TableCell className={classes.td} align="right">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            style={{ textTransform: 'none', fontSize: 12.5, borderRadius: 9, borderColor: `${ORANGE}66`, color: ORANGE_DARK }}
                                                            onClick={(e) => { e.stopPropagation(); openHistory(job) }}
                                                        >
                                                            View
                                                        </Button>
                                                    </TableCell>
                                                    {/* Only the jobs that did not complete get a re-run.
                                                        A healthy job has nothing to repeat, and offering
                                                        the button there is how a report gets sent twice. */}
                                                    <TableCell className={classes.td} align="right">
                                                        {canRerun(job) ? (
                                                            <Tooltip arrow title={`Run ${job.label} now, without waiting for the next scheduled run`}>
                                                                <span>
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        disableElevation
                                                                        className={classes.rerunBtn}
                                                                        disabled={Boolean(triggering)}
                                                                        startIcon={triggering === job.jobName
                                                                            ? <CircularProgress size={13} thickness={5} style={{ color: '#fff' }} />
                                                                            : <PlayArrowIcon style={{ fontSize: 16 }} />}
                                                                        onClick={(e) => { e.stopPropagation(); setConfirmJob(job) }}
                                                                    >
                                                                        {triggering === job.jobName ? 'Running…' : 'Re-run'}
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <span className={classes.stripEmpty}>—</span>
                                                        )}
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow>
                                                    <TableCell className={classes.tdTight} colSpan={10}>
                                                        <Collapse in={open} timeout="auto" unmountOnExit>
                                                            <div className={classes.detail}>
                                                                {job.lastStatus === 'failed' && job.lastError ? (
                                                                    <>
                                                                        <div className={classes.detailLabel}>Why the last run failed</div>
                                                                        <div className={classes.errorBox}>{job.lastError}</div>
                                                                    </>
                                                                ) : null}

                                                                <div className={classes.detailGrid}>
                                                                    <div>
                                                                        <div className={classes.detailLabel}>Endpoint</div>
                                                                        <div className={`${classes.detailValue} ${classes.mono}`}>
                                                                            {(job.method || '').toUpperCase()} {job.endpoint || '-'}
                                                                            {job.endpoint ? (
                                                                                <Tooltip title="Copy URL" arrow>
                                                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); copy(job.endpoint) }}>
                                                                                        <FileCopyOutlinedIcon style={{ fontSize: 14 }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className={classes.detailLabel}>Runs in window</div>
                                                                        <div className={classes.detailValue}>
                                                                            {job.runs}{job.expected ? ` of about ${job.expected} expected` : ''}
                                                                            {' · '}{job.failures} failed
                                                                            {job.skipped ? ` · ${job.skipped} skipped` : ''}
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className={classes.detailLabel}>Average duration</div>
                                                                        <div className={classes.detailValue}>{fmtDuration(job.avgDurationMs)}</div>
                                                                    </div>
                                                                    <div>
                                                                        <div className={classes.detailLabel}>Last run</div>
                                                                        <div className={classes.detailValue}>{fmtWhen(job.lastRunAt)} ({fmtAgo(job.lastRunAt)})</div>
                                                                    </div>
                                                                </div>

                                                                {job.lastResponse ? (
                                                                    <>
                                                                        <div className={classes.detailLabel}>What the API returned</div>
                                                                        <div className={classes.responseBox}>
                                                                            {typeof job.lastResponse === 'string'
                                                                                ? job.lastResponse
                                                                                : JSON.stringify(job.lastResponse, null, 2)}
                                                                        </div>
                                                                    </>
                                                                ) : null}

                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    disableElevation
                                                                    style={{ textTransform: 'none', background: ORANGE_DARK, color: '#fff', borderRadius: 9, fontSize: 12.5 }}
                                                                    onClick={(e) => { e.stopPropagation(); openHistory(job) }}
                                                                >
                                                                    Open full run history
                                                                </Button>
                                                            </div>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            {/* ---------------------------- drill-down ---------------------------- */}
            <Dialog open={!!historyJob} onClose={() => setHistoryJob(null)} maxWidth="md" fullWidth>
                <div className={classes.dialogHead}>
                    <div className={classes.dialogHeadText}>
                        <div className={classes.dialogTitle}>{historyJob?.label}</div>
                        <div className={classes.dialogSub}>
                            {historyJob?.scheduleText} · {(historyJob?.method || '').toUpperCase()} {historyJob?.endpoint}
                        </div>
                    </div>
                    <Tooltip title="Close">
                        <IconButton
                            size="small"
                            aria-label="Close"
                            className={classes.dialogClose}
                            onClick={() => setHistoryJob(null)}
                        >
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>
                </div>
                <DialogContent style={{ padding: '18px 22px' }}>
                    {historyLoading ? (
                        <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress size={26} style={{ color: ORANGE_DARK }} /></div>
                    ) : !history.length ? (
                        <div style={{ padding: 36, textAlign: 'center', color: INK_MUTED }}>No runs recorded for this job yet.</div>
                    ) : (
                        <>
                            <div className={classes.panelTitle} style={{ marginBottom: 2 }}>
                                <TimerIcon style={{ fontSize: 18, color: ORANGE_DARK }} /> How long each run took
                            </div>
                            <div className={classes.panelSub}>Last {history.length} runs, oldest first</div>
                            <div style={{ height: 190, marginBottom: 18 }}>
                                <Line data={durationSeries} options={durationOptions} />
                            </div>
                            <Divider style={{ marginBottom: 12 }} />
                            <TableContainer style={{ maxHeight: 340 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.thSub}>Status</TableCell>
                                            <TableCell className={classes.thSub}>Started</TableCell>
                                            <TableCell className={classes.thSub}>Duration</TableCell>
                                            <TableCell className={classes.thSub}>HTTP</TableCell>
                                            <TableCell className={classes.thSub}>Result</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {history.map((run) => (
                                            <TableRow key={run._id}>
                                                <TableCell className={classes.td}><StatusChip status={run.status} classes={classes} small /></TableCell>
                                                <TableCell className={classes.td}>
                                                    <div>{fmtWhen(run.startedAt)}</div>
                                                    <div className={classes.tileHint}>
                                                        {fmtAgo(run.startedAt)}{run.triggeredBy === 'manual' ? ' · manual' : ''}
                                                    </div>
                                                </TableCell>
                                                <TableCell className={classes.td}>{fmtDuration(run.durationMs)}</TableCell>
                                                <TableCell className={classes.td}>
                                                    {run.httpStatus || (run.errorCode ? <span className={classes.mono}>{run.errorCode}</span> : '-')}
                                                </TableCell>
                                                <TableCell className={classes.td} style={{ maxWidth: 320 }}>
                                                    {run.status === 'failed'
                                                        ? <span style={{ color: RED }}>{run.errorMessage || 'Failed'}</span>
                                                        : (
                                                            <span className={classes.mono} style={{ color: INK_SOFT }}>
                                                                {run.response
                                                                    ? (typeof run.response === 'string'
                                                                        ? run.response
                                                                        : JSON.stringify(run.response)).slice(0, 160)
                                                                    : '-'}
                                                            </span>
                                                        )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '10px 20px 16px' }}>
                    {/* Was a bare grey text button, which does not read as a
                        control at the end of a long scrolling dialog. */}
                    <Button
                        onClick={() => setHistoryJob(null)}
                        variant="outlined"
                        startIcon={<ClearIcon style={{ fontSize: 16 }} />}
                        style={{
                            textTransform: 'none', fontWeight: 600, borderRadius: 9,
                            borderColor: `${ORANGE}66`, color: ORANGE_DARK,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* These jobs call real endpoints - some of them send messages or
                post data - so the target is spelled out before anything fires,
                rather than the click being the whole confirmation. */}
            <Dialog open={!!confirmJob} onClose={() => setConfirmJob(null)} maxWidth="xs" fullWidth>
                <div className={classes.banner} style={{ borderRadius: 0, marginBottom: 0 }}>
                    <div className={classes.bannerIcon}><PlayArrowIcon style={{ fontSize: 24 }} /></div>
                    <div className={classes.dialogHeadText}>
                        <div className={classes.bannerTitle} style={{ fontSize: 17 }}>Run this job now?</div>
                        <div className={classes.bannerSub}>{confirmJob?.label}</div>
                    </div>
                    <Tooltip title="Close">
                        <IconButton
                            size="small"
                            aria-label="Close"
                            className={classes.dialogClose}
                            onClick={() => setConfirmJob(null)}
                        >
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>
                </div>
                <DialogContent style={{ padding: '18px 22px' }}>
                    <div className={classes.detailLabel}>Endpoint</div>
                    <div className={`${classes.responseBox} ${classes.mono}`} style={{ maxHeight: 90 }}>
                        {confirmJob?.endpoint || 'Not recorded — the server will refuse this.'}
                    </div>
                    <div className={classes.confirmBox}>
                        <WarningIcon style={{ color: AMBER, fontSize: 20 }} />
                        <div className={classes.confirmNote}>
                            This calls the endpoint immediately, outside its schedule. If the job
                            sends messages or writes data, running it again repeats that.
                            The attempt is logged in the run history as a manual run.
                        </div>
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '10px 20px 16px' }}>
                    <Button onClick={() => setConfirmJob(null)} style={{ textTransform: 'none', color: INK_SOFT }}>Cancel</Button>
                    <Button
                        className={classes.rerunBtn}
                        startIcon={<PlayArrowIcon style={{ fontSize: 16 }} />}
                        onClick={() => runJobNow(confirmJob)}
                    >
                        Run now
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default connect(null, { setSnackInfo })(CronJobMonitor)
